/**
 * Curated catalog of seamless, tileable SVG background patterns for the Dream Builder.
 *
 * Each entry is an original geometric motif authored in-house (no third-party pattern
 * artwork) so the catalog carries no external licensing requirements. `render` receives a
 * fully-resolved color string (already including opacity as rgba) and returns raw SVG markup
 * sized to `tileSize`. The builder URL-encodes the result into a CSS background-image layer.
 */

export type PatternTemplate = {
  id: string;
  name: string;
  /** Intrinsic tile size in px (square). Multiplied by the config `scale` at render time. */
  tileSize: number;
  /** Returns raw SVG markup with `color` applied to the motif's fill or stroke. */
  render: (color: string) => string;
};

const svg = (size: number, body: string): string =>
  `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'>${body}</svg>`;

export const PATTERN_CATALOG = [
  {
    id: 'dots',
    name: 'Dots',
    tileSize: 20,
    render: (c: string) => svg(20, `<circle cx='10' cy='10' r='2.5' fill='${c}'/>`),
  },
  {
    id: 'rings',
    name: 'Rings',
    tileSize: 20,
    render: (c: string) => svg(20, `<circle cx='10' cy='10' r='6' fill='none' stroke='${c}' stroke-width='1.5'/>`),
  },
  {
    id: 'grid',
    name: 'Grid',
    tileSize: 24,
    render: (c: string) => svg(24, `<path d='M0 0H24M0 0V24' fill='none' stroke='${c}' stroke-width='1'/>`),
  },
  {
    id: 'vertical-lines',
    name: 'Vertical lines',
    tileSize: 16,
    render: (c: string) => svg(16, `<path d='M0 0V16' stroke='${c}' stroke-width='1'/>`),
  },
  {
    id: 'horizontal-lines',
    name: 'Horizontal lines',
    tileSize: 16,
    render: (c: string) => svg(16, `<path d='M0 0H16' stroke='${c}' stroke-width='1'/>`),
  },
  {
    id: 'diagonal-lines',
    name: 'Diagonal lines',
    tileSize: 20,
    render: (c: string) =>
      svg(20, `<path d='M0 20 L20 0 M-1 1 L1 -1 M19 21 L21 19' fill='none' stroke='${c}' stroke-width='1.5'/>`),
  },
  {
    id: 'crosshatch',
    name: 'Crosshatch',
    tileSize: 20,
    render: (c: string) =>
      svg(
        20,
        `<path d='M0 20 L20 0 M-1 1 L1 -1 M19 21 L21 19 M0 0 L20 20 M19 -1 L21 1 M-1 19 L1 21' fill='none' stroke='${c}' stroke-width='1'/>`,
      ),
  },
  {
    id: 'checkerboard',
    name: 'Checkerboard',
    tileSize: 20,
    render: (c: string) => svg(20, `<path d='M0 0h10v10H0zM10 10h10v10H10z' fill='${c}'/>`),
  },
  {
    id: 'plus',
    name: 'Plus',
    tileSize: 24,
    render: (c: string) => svg(24, `<path d='M11 7h2v4h4v2h-4v4h-2v-4H7v-2h4z' fill='${c}'/>`),
  },
  {
    id: 'diamonds',
    name: 'Diamonds',
    tileSize: 20,
    render: (c: string) => svg(20, `<path d='M10 3 L17 10 L10 17 L3 10 Z' fill='${c}'/>`),
  },
  {
    id: 'zigzag',
    name: 'Zigzag',
    tileSize: 20,
    render: (c: string) => svg(20, `<path d='M0 14 L10 6 L20 14' fill='none' stroke='${c}' stroke-width='2'/>`),
  },
  {
    id: 'waves',
    name: 'Waves',
    tileSize: 20,
    render: (c: string) => svg(20, `<path d='M0 10 Q5 4 10 10 T20 10' fill='none' stroke='${c}' stroke-width='1.5'/>`),
  },
  {
    id: 'triangles',
    name: 'Triangles',
    tileSize: 20,
    render: (c: string) => svg(20, `<path d='M10 4 L17 16 L3 16 Z' fill='${c}'/>`),
  },
  {
    id: 'confetti',
    name: 'Confetti',
    tileSize: 28,
    render: (c: string) =>
      svg(
        28,
        `<g fill='${c}'><circle cx='4' cy='6' r='1.5'/><circle cx='18' cy='3' r='1.5'/><circle cx='24' cy='14' r='1.5'/><circle cx='9' cy='18' r='1.5'/><circle cx='15' cy='24' r='1.5'/><circle cx='2' cy='26' r='1.5'/></g>`,
      ),
  },
  {
    id: 'stars',
    name: 'Stars',
    tileSize: 24,
    render: (c: string) =>
      svg(
        24,
        `<path d='M12 3 L14.1 9.1 L20.6 9.2 L15.4 13.1 L17.3 19.3 L12 15.6 L6.7 19.3 L8.6 13.1 L3.4 9.2 L9.9 9.1 Z' fill='${c}'/>`,
      ),
  },
  {
    id: 'diagonal-stripes',
    name: 'Diagonal stripes',
    tileSize: 20,
    render: (c: string) =>
      svg(
        20,
        `<path d='M0 0 L5 0 L0 5 Z M10 0 L15 0 L0 15 L0 10 Z M20 0 L20 5 L5 20 L0 20 Z M20 10 L20 15 L15 20 L10 20 Z' fill='${c}'/>`,
      ),
  },
  {
    id: 'dashes',
    name: 'Dashes',
    tileSize: 16,
    render: (c: string) =>
      svg(16, `<path d='M2 4 H9 M7 12 H14' fill='none' stroke='${c}' stroke-width='1.5' stroke-linecap='round'/>`),
  },
  {
    id: 'brick',
    name: 'Brick',
    tileSize: 24,
    render: (c: string) =>
      svg(
        24,
        `<path d='M0 0 H24 M0 12 H24 M0 24 H24 M0 0 V12 M24 0 V12 M12 12 V24' fill='none' stroke='${c}' stroke-width='1.5'/>`,
      ),
  },
  {
    id: 'scallops',
    name: 'Scallops',
    tileSize: 24,
    render: (c: string) =>
      svg(
        24,
        `<g fill='none' stroke='${c}' stroke-width='1.4'><path d='M-12 0 a12 12 0 0 0 24 0 M-8 0 a8 8 0 0 0 16 0 M-4 0 a4 4 0 0 0 8 0'/><path d='M12 0 a12 12 0 0 0 24 0 M16 0 a8 8 0 0 0 16 0 M20 0 a4 4 0 0 0 8 0'/><path d='M0 12 a12 12 0 0 0 24 0 M4 12 a8 8 0 0 0 16 0 M8 12 a4 4 0 0 0 8 0'/><path d='M-24 12 a12 12 0 0 0 24 0 M-20 12 a8 8 0 0 0 16 0 M-16 12 a4 4 0 0 0 8 0'/><path d='M24 12 a12 12 0 0 0 24 0 M28 12 a8 8 0 0 0 16 0'/></g>`,
      ),
  },
] as const satisfies readonly PatternTemplate[];

export type PatternId = (typeof PATTERN_CATALOG)[number]['id'];

export const getPatternTemplate = (id: string): PatternTemplate | undefined =>
  PATTERN_CATALOG.find((pattern) => pattern.id === id);
